import { BaseEvent, LensEvent } from "@me8eon/events";
import { AsyncState, isDataAs } from "@me8eon/async";

import { createContext, ReactElement, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { capitalizeFirstLetter } from "@me8eon/string_utils";
import { isErrors } from "@me8eon/errors";
import { InterpreterPlugIn } from "@me8eon/interpreter";
import { useThrowError } from "@me8eon/throw_error";
import { AppendAndGetAsyncStore } from "@me8eon/async_storage";
import { useDebug } from "@me8eon/debug";

export const eventStoreDebug = "eventStore";
export type EventAsyncStore<Id> = AppendAndGetAsyncStore<Id, LensEvent>
export type AsyncStateEventStore<T> = {
    change: (...e: LensEvent[]) => void
    state: AsyncState<T>
    events: AsyncState<BaseEvent[]>
    reset: () => void
}


export function makeContextForEventStore<Field extends string, Id, T>(field: Field, interpreter: InterpreterPlugIn<T>, useId: () => Id | undefined) {
    // Create the context dynamically
    const context = createContext<AsyncStateEventStore<T> | undefined>(undefined);

    function useField(): AsyncStateEventStore<T> {
        const contextValue = useContext(context);
        const throwError = useThrowError();
        if (contextValue === undefined) {
            const upperedName = capitalizeFirstLetter(field);
            return throwError("s/w", `use${upperedName} must be used within a ${upperedName}Provider`);
        }
        return contextValue!;
    }

    type ProviderProps = { children: ReactNode } & Record<Field, EventAsyncStore<Id>>;

    function Provider(props: ProviderProps): ReactElement {
        const { children } = props;
        const id = useId(); //If you get an error here, are you inside the provider this needs?
        const asyncStore: EventAsyncStore<Id> = props[field];
        const [state, setState] = useState<AsyncState<T>>({ loading: true });
        const [events, setEvents] = useState<AsyncState<BaseEvent[]>>({ loading: true });
        const reportError = useThrowError();
        const debug = useDebug(eventStoreDebug);

        function reset() {
            const { execute, initial } = interpreter;
            debug("resetting", id);
            setState({ loading: true });
            setEvents({ loading: true });
            debug("calling async store");
            if (id)
                asyncStore.get(id).then(async (events) => {
                    debug("async store returned", events);
                    if (isErrors(events)) {
                        debug("setting errors", events);
                        setState({ errors: events.errors });
                    } else {
                        setEvents({ data: events.value });
                        const value = await execute(events.value, initial);
                        if (isErrors(value)) setState({ errors: value.errors });
                        else setState({ data: value.value });
                    }
                });
        }

        useEffect(reset, [asyncStore, id, interpreter]);

        const change = useMemo(() => async (...es: LensEvent[]) => {
            const { execute } = interpreter;
            if (isDataAs(state) && isDataAs(events) && id) {
                const newValue = await execute(es, state.data);
                if (isErrors(newValue)) setState({ errors: newValue.errors });
                else {
                    const newEvents = await asyncStore.append(id, ...es);
                    if (isErrors(newEvents))
                        setEvents({ errors: newEvents.errors });
                    else {
                        setState({ data: newValue.value });
                        setEvents({ data: [...events.data, ...es] });
                    }
                }
            } else {// this is bad. We have just lost data...
                return reportError("state", `state is not suitable for change. Id is ${id}. State is ${JSON.stringify(state)}`);
            }
        }, [asyncStore, id, interpreter, events, state]);

        const value: AsyncStateEventStore<any> = { change, state, events, reset };
        return <context.Provider value={value}> {children} </context.Provider>;
    }

    return { use: useField, Provider, context };
}